import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, throwError } from 'rxjs';
import { TravelContract } from 'src/app/travel-contracts/travel-contracts-grid/models/travel-contract';
import { TravelContractsService } from 'src/app/travel-contracts/travel-contracts-service';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ContractBillingInfo } from 'src/app/travel-contracts/contract-profile/shared/models/contract-billing-info.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import {
    ContractType,
    ContractStatus,
    ContractSchedule
} from 'src/app/travel-contracts/travel-contracts-grid/enums/contract-type.enum';
import { FacilityBillingRules } from 'src/app/facility-contracts/facilities/models/facility-billing-rules.model';
import { FacilityBillingService } from 'src/app/facility-contracts/facility-billing.service';
import { FacilityBillingRule } from 'src/app/facility-contracts/facilities/models/facility-billing-rule.model';
import { ContractAgency } from 'src/app/travel-contracts/travel-contracts-grid/models/contract-agency.model';
import { MultiplierType } from 'src/app/travel-contracts/travel-contracts-grid/enums/multiplier-type';
import { catchError, map, mergeMap, share, switchMap, tap } from 'rxjs/operators';
import { EffectiveDatesSummaryComponent } from 'src/app/travel-contracts/effective-dates/effective-dates-summary.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EffectiveDatesHistoryComponent } from 'src/app/travel-contracts/effective-dates/effective-dates-history.component';
import { BillingPolicy } from 'src/app/travel-contracts/effective-dates/model/billing-policy.model';
import { EffectiveDatesService } from 'src/app/travel-contracts/effective-dates/effective-dates.service';
import { TimeTrackingStatus } from 'src/app/travel-contracts/effective-dates/model/time-tracking-status.enum';
import dayjs from 'dayjs';
import { BillingRates } from 'src/app/travel-contracts/effective-dates/model/billing-rates.model';
import { BillingRuleMultiplier } from 'src/app/facility-contracts/facilities/models/billing-rule-multiplier.model';
import { debounce } from 'src/app/shared/utilities/debounce';
import { Margin } from 'src/app/shared/models/margin.model';
import { MarginDifference } from 'src/app/shared/lib/contract-request/models/contract-request-margin-differences.model';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { NonBillableOrientationHours } from '../../../../contract-profile/shared/models/non-billable-orientation-hours.model';
import Big, { RoundingMode } from 'big.js';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MarginEffectiveDateGroupId } from 'src/app/travel-contracts/effective-dates/model/margin-effective-date-group-id.model';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { PathfindersFlags } from 'src/app/shared/models/enums/team-feature-flags/pathfinders-feature-flags.enum';
import { PayrollTimecardFeatureFlags } from 'src/app/shared/models/enums/team-feature-flags/payroll-timecard-feature-flags.enum';

@UntilDestroy()
@Component({
    selector: 'nova-billing-profile',
    templateUrl: './billing-profile.component.html',
    styleUrls: ['./billing-profile.component.scss']
})
export class BillingProfileComponent implements OnInit {
    @Input()
    contract: TravelContract;

    @Input()
    isEditRates = true;

    @Input() margin: Margin = null;
    @Input() marginDifferences: MarginDifference[] = null;
    @ViewChild('agencyRegularPay') agencyRegularPayInput: ElementRef;
    @ViewChild('billRatesSection') private billRatesSection: ElementRef;

    billingInfo$: Observable<ContractBillingInfo>;
    contractBillingInfoForm: UntypedFormGroup;
    billingRules: FacilityBillingRules;
    documentId: number;
    agencyName = null;
    isNonStandardShiftWithInfoFlag = false;
    displayLocumPayrollEffectiveDatesHistoryFlag = false;
    isLocum = false;
    private reloadTrigger = new BehaviorSubject(true);
    effectiveDatesSummaryRef: MatDialogRef<EffectiveDatesSummaryComponent>;
    previousValues: ContractBillingInfo;
    initialValues: ContractBillingInfo;
    isNew: boolean;
    effectiveDatesHistoryRef: MatDialogRef<EffectiveDatesHistoryComponent>;
    policyId: number;
    startDate: Date;
    contractStartDate: Date;
    contractEndDate: Date;
    defaultStartDate: Date;
    pendingSave: boolean;
    hasSavedBillingRate = false;
    initialLoading = false;
    invalidDates = ['1900-01-01T00:00:00', '0001-01-01T00:00:00'];

    summaryGroups = {
        billRates: [
            'billRegRate',
            'billRegularRate',
            'billOverTimeRate',
            'billOtRate',
            'billDoubleTimeRate',
            'billDtRate',
            'billChargeRate',
            'billOnCallRate',
            'billCallBackRate',
            'billHolidayRate',
            'billNightCallRate',
            'billOnCall24HourRate',
            'billWeekendRate'
        ],
        agencyPay: [
            'agencyRegPay',
            'agencyRegularRate',
            'agencyOTPay',
            'agencyOTRate',
            'agencyDTPay',
            'agencyDTRate',
            'agencyChargePay',
            'agencyChargeRate',
            'agencyCallPay',
            'agencyOnCallRate',
            'agencyCBPay',
            'agencyCallBackRate',
            'agencyHOLPay',
            'agencyHolidayRate',
            'agencyNightCallPay',
            'agencyNightCallRate',
            'agencyOnCall24HourPay',
            'agencyOnCall24HourRate',
            'agencyWeekendPay',
            'agencyWeekendRate'
        ],
        overtimeRules: [
            'billOverTimeDayRule',
            'otDayRule',
            'billOverTimeWeekRule',
            'otWeekRule',
            'billOverTimeTwoWeekRule',
            'oT2WeekRule'
        ],
        doubletimeRules: [
            'billDoubleTimeDayRule',
            'dtDayRule',
            'billDoubleTimeWeekRule',
            'dtWeekRule',
            'billDoubleTimeTwoWeekRule',
            'dT2WeekRule'
        ]
    };

    billRatesCurrentInfo: string;
    billRatesFutureInfo: string;
    billRatesPastInfo: string;
    agencyPayCurrentInfo: string;
    agencyPayFutureInfo: string;
    agencyPayPastInfo: string;
    overtimeRulesFutureInfo: string;
    doubletimeRulesFutureInfo: string;
    debounceTime: number = 500;
    billRates: BillingRates;
    nonBillableOrientation: NonBillableOrientationHours[] = null;
    billRateEffectiveDate: Date;
    private _shiftMultiplier: BillingRuleMultiplier;
    protected readonly baseBillRate = 'Base Bill Rate';
    protected readonly otBillRate = 'OT Bill Rate';
    protected readonly premiumBillRate = 'Premium Bill Rate';
    protected readonly callbackBillRate = 'Callback Bill Rate';
    protected readonly holidayBillRate = 'Holiday Bill Rate';
    protected readonly onCallBillRate = 'On Call Bill Rate';
    protected readonly chargeBill = 'Charge Bill';
    protected readonly totalChargeBill = 'Total Charge Bill';
    readonly featureFlag = FeatureFlag;

    get shiftMultiplier(): BillingRuleMultiplier {
        return this._shiftMultiplier;
    }

    constructor(
        private readonly billingService: FacilityBillingService,
        private readonly travelContractsService: TravelContractsService,
        private readonly formbuilder: UntypedFormBuilder,
        private readonly toasterService: ToasterService,
        private readonly _dialog: MatDialog,
        private readonly effectiveDatesService: EffectiveDatesService,
        protected readonly _lDFeatureManager: LDFeatureManager
    ) {
        this.saveBillingInfo = debounce(this.saveBillingInfo, this.debounceTime);
    }

    ngOnInit(): void {
        this.attachFeatureFlag(PathfindersFlags.BillingProfileRotatingShiftFix);
        this.attachFeatureFlag(PayrollTimecardFeatureFlags.DisplayLocumPayrollEffectiveDatesHistory);

        this.isLocum = this.contract?.isLocum;

        if (this.contract.isSubcontractor) {
            this.travelContractsService.getAgency(this.contract.id).subscribe((data: ContractAgency | null) => {
                this.agencyName = data ? data.agencyName : null;
            });
        }

        this.contractBillingInfoForm = this.createForm();

        if (this.contract) {
            this.billingInfo$ = this.reloadTrigger.pipe(
                switchMap(() => this.effectiveDatesService.getEffectiveDatesHistory(this.contract.id)),
                tap((data) => {
                    this.contractStartDate = data.contractStartDate;
                    this.contractEndDate = data.contractEndDate;
                    this.billRates = data;
                    this.fillNotesInfo(data);
                }),
                switchMap(() =>
                    this.travelContractsService.getBillingInfo(this.contract.id.toString()).pipe(
                        tap((contractBillingInfo) => (this.previousValues = { ...contractBillingInfo })),
                        map((x: ContractBillingInfo) => this.setDefaultsFromMarginDifferences(x))
                    )
                ),
                share()
            );

            combineLatest([this.billingInfo$, this.billingService.getRulesByFacilityId(this.contract.facilityId)])
                .pipe(
                    map(([billingInfo, billingRules]) => ({ billingInfo, billingRules })),
                    untilDestroyed(this)
                )
                .subscribe((x) => {
                    if (x.billingInfo !== null) {
                        this.documentId = x.billingInfo.documentId;
                        this.contractBillingInfoForm.patchValue(x.billingInfo);
                        this.formatDecimal('billRegRate');
                        this.formatDecimal('billOnCallRate');
                        this.formatDecimal('billOverTimeRate');
                        this.formatDecimal('billCallBackRate');
                        this.formatDecimal('billDoubleTimeRate');
                        this.formatDecimal('billHolidayRate');
                        this.formatDecimal('billChargeRate');
                        this.formatDecimal('billNightCallRate');
                        this.formatDecimal('billOnCall24HourRate');
                        this.formatDecimal('billWeekendRate');

                        this.policyId = x.billingInfo.policyId;
                        this.startDate = x.billingInfo.startDate;
                    } else {
                        //In new flow with setDefaultsFromMarginDifferences billingInfo will never be null here
                        this.isNew = true;
                    }
                    if (x.billingInfo !== null && (this.contract.isSubcontractor || !this.contract.isAyaBrand)) {
                        this.formatDecimal('agencyCallPay');
                        this.formatDecimal('agencyCBPay');
                        this.formatDecimal('agencyChargePay');
                        this.formatDecimal('agencyDTPay');
                        this.formatDecimal('agencyHOLPay');
                        this.formatDecimal('agencyOTPay');
                        this.formatDecimal('agencyRegPay');
                        this.formatDecimal('agencyNightCallPay');
                        this.formatDecimal('agencyOnCall24HourPay');
                        this.formatDecimal('agencyWeekendPay');
                    }

                    if (this.billRates?.nonBillableOrientationHours) {
                        this.contractBillingInfoForm
                            .get('nonBillableOrientationHours')
                            .setValue(this.billRates.nonBillableOrientationHours);
                    }

                    this.initialValues = this.contractBillingInfoForm.value as ContractBillingInfo;
                    this.billingRules = x.billingRules;

                    if (this.marginDifferencesForBillingsOnly()) {
                        this.scrollToBillRateSection();

                        if (this.hasBillingRateFromMarginDifferences()) {
                            const isAgencyPay = this.contract.isSubcontractor || !this.contract.isAyaBrand;
                            if (!this.hasSavedBillingRate) {
                                this.initialLoading = true;
                                this.calculateDefaultRates(isAgencyPay);
                            }
                        }
                    }
                    this.hasSavedBillingRate = false;
                });
        }
    }

    createForm(): UntypedFormGroup {
        return this.formbuilder.group({
            payType: [1],
            billRegRate: [0],
            billOverTimeRate: [0],
            billDoubleTimeRate: [0],
            billChargeRate: [0],
            billOnCallRate: [0],
            billCallBackRate: [0],
            billHolidayRate: [0],
            billNightCallRate: [0],
            billOnCall24HourRate: [0],
            billWeekendRate: [0],
            billOverTimeDayRule: [0],
            billOverTimeWeekRule: [0],
            billOverTimeTwoWeekRule: [0],
            billDoubleTimeDayRule: [0],
            billDoubleTimeWeekRule: [0],
            billDoubleTimeTwoWeekRule: [0],
            specialRate: [false],
            billOrientation: [0],
            billOrientation2: [0],
            notes: [''],
            agencyCallPay: [0],
            agencyCBPay: [0],
            agencyChargePay: [0],
            agencyDTPay: [0],
            agencyHOLPay: [0],
            agencyOTPay: [0],
            agencyRegPay: [0],
            agencyNightCallPay: [0],
            agencyOnCall24HourPay: [0],
            agencyWeekendPay: [0],
            nonBillableOrientationHours: this.formbuilder.array([
                this.addNonBillableOrientationHoursGroup(1),
                this.addNonBillableOrientationHoursGroup(2)
            ])
        });
    }

    addNonBillableOrientationHoursGroup(order: number): UntypedFormGroup {
        return this.formbuilder.group({
            id: [0],
            contractId: [this.contract.id],
            billableHours: [null],
            billableRate: [null],
            billableRateType: [''],
            sortOrder: [order]
        });
    }

    /**
     * Gets the correct Shift Mulitplier, based on the field:  contractShiftHours1
     * @param rule
     * @returns
     */
    private getShiftMultiplier(rule: FacilityBillingRule): BillingRuleMultiplier {
        const multiplierList = rule.multipliers
            .filter((x) => parseInt(x.name, 10) <= this.contract.contractShiftHours1)
            .sort((a, b) => parseInt(b.name, 10) - parseInt(a.name, 10));
        if (multiplierList.length) {
            this._shiftMultiplier = multiplierList[0];
        } else {
            this._shiftMultiplier = rule.multipliers[0];
        }
        return this._shiftMultiplier;
    }

    /**
     * This calculates the Rate for each Billing Rule (ie: Overtime, Double Time,...) and sets it.
     * The algorithm is as follows ==>
     *
     * ACTIVE RULE
     * 1.  If the rule is active, but set as "no multiplier", or has a set multiplier but no "amount"
     * then RATE is:
     * - Overtime = NaN (changes to Regular Rate when saved)
     * - Double Time = NaN (changes to Regular Rate when saved)
     * - Charge = NaN (changes to $0.00 when saved)
     * - On Call = NaN (changes to $0.00 when saved)
     * - Call Back = NaN (changes to $0.00 when saved)
     * - Holiday = NaN (changes to Regular Rate when saved)
     *
     * 2. Rule has amount so calculate rate with:  calculateDefaultOrShiftMultipliers()
     *
     * NOT ACTIVE RULE
     * If rule is "Overtime, Holiday" then RATE=RegularRate
     *
     * @param rule
     * @param rateControlName
     * @param regularRate
     * @param roundingMode
     */
    calculateRate(
        rule: FacilityBillingRule,
        rateControlName: string,
        regularRate: number,
        roundingMode?: RoundingMode
    ): void {
        if (!rule) {
            return;
        }

        let rate = NaN;
        const convertedRates = this.convertNaNData();
        const convertedRate = convertedRates.get(rateControlName).value;
        if (!isNaN(regularRate)) {
            // ACTIVE Rule
            if (rule.checked && rule?.multipliers.length > 0) {
                // DefaultMultipler="No Multiplier" case
                if (this.isNoMultiplier(rule)) {
                    if (this.initialLoading) {
                        rate = convertedRate;
                    } else {
                        rate = NaN;
                    }
                } else {
                    // DefaultMultiplier has Amount or Shift Multipliers exists (ie: 8 hr, 10 hr, and/or 12 hr)
                    rate = this.calculateDefaultOrShiftMultipliers(rule, regularRate, convertedRate, roundingMode);
                }
            } else {
                rate = this.calculateInactiveRate(rule.name, regularRate);
            }
        }

        this.contractBillingInfoForm.get(rateControlName).setValue(rate);
        this.formatDecimal(rateControlName);
    }

    /**
     * We have the amount and need to calculate the rate now.
     *
     * 1. Get the correct Shift Multipler (ie: Default or 8,10,12 shift)
     * 2. If multiplier is "Dollar Type" and rule is "On Call", then RATE=amount
     * 3. If multiplier is "Dollar Type" then RATE=amount+RegularRate
     * 4. If multiplier is "Percentage Type" then RATE=amount*RegularRate
     * @param rule
     * @param regularRate
     * @param roundingMode
     * @returns
     */
    calculateDefaultOrShiftMultipliers(
        rule: FacilityBillingRule,
        regularRate: number,
        convertedRate: number,
        roundingMode?: RoundingMode
    ): number {
        let rate = 0;
        const ruleName = rule.name;
        const multiplier = this.getShiftMultiplier(rule);
        const amount = multiplier?.amount ? multiplier?.amount : null;

        if (multiplier?.type === 0) {
            if (this.initialLoading) {
                rate = convertedRate;
            } else {
                rate = NaN;
            }
            return rate;
        }

        if (!amount || amount < 0) {
            console.error('Should not get here!');
            console.error(` ===>  ${rule}`);
            return rate;
        }

        if (multiplier.type === MultiplierType.Dollar) {
            rate = this.calculateDollarRate(ruleName, regularRate, amount);
        } else if (multiplier.type === MultiplierType.Percentage) {
            rate = new Big(multiplier.amount).times(regularRate).round(2, roundingMode).toNumber();
        } else {
            console.error('Should not get here!');
            console.error(` ===>  ${rule}`);
        }
        return rate;
    }

    /**
     * Special case where:  It's a Default Multipler (no shift multipliers) and there is no amount entered.
     * @param rule
     * @returns
     */
    private isNoMultiplier(rule: FacilityBillingRule): boolean {
        return rule.multipliersAdded === false && rule?.multipliers.length === 1 && rule.multipliers[0]?.type === 0;
    }

    /**
     * The rule is NOT active (aka "No Rules"):
     * 1. If rule is OverTime, Holiday then rate=RegularRate
     * 2. Else, rate=0
     * @param ruleName
     * @param regularRate
     * @returns
     */
    private calculateInactiveRate(ruleName: string, regularRate: number): number {
        let rate = 0;
        const regularRateList = ['overtime', 'holiday'];
        if (regularRateList.includes(ruleName.replace(' ', '').toLowerCase())) {
            rate = regularRate;
        }
        return rate;
    }

    /**
     * The rule is ACTIVE and multiplierType=DOLLAR
     * 1. If multiplier is "Dollar Type" and rule is "On Call", then RATE=amount
     * 2. If multiplier is "Dollar Type" then RATE=amount+RegularRate
     * @param ruleName
     * @param regularRate
     * @param amount
     * @returns
     */
    private calculateDollarRate(ruleName: string, regularRate: number, amount: number): number {
        const onCallRule = 'On Call';
        let rate = 0;
        // Dollar BUMP, rule is "OnCall"
        if (ruleName === onCallRule) {
            rate = amount;
        } else {
            // Rules are:  OverTime, DoubleTime, Charge Nurse, Callback, Holiday
            rate = amount + regularRate;
        }
        return rate;
    }

    private calculateFreq(
        rule: FacilityBillingRule,
        inADayControlName: string,
        inAWeekControlName: string,
        in2WeeksControlName: string
    ) {
        if (rule && rule.multipliers.length) {
            const multiplier = this.getShiftMultiplier(rule);
            const inADay = rule.checked
                ? multiplier.freq1 === 0
                    ? multiplier.hours1
                    : multiplier.freq2 === 0
                      ? multiplier.hours2
                      : 0
                : 0;
            const inAWeek = rule.checked
                ? multiplier.freq1 === 1
                    ? multiplier.hours1
                    : multiplier.freq2 === 1
                      ? multiplier.hours2
                      : 0
                : 0;
            const in2Weeks = rule.checked
                ? multiplier.freq1 === 2
                    ? multiplier.hours1
                    : multiplier.freq2 === 2
                      ? multiplier.hours2
                      : 0
                : 0;
            this.contractBillingInfoForm.get(inADayControlName).setValue(inADay);
            this.contractBillingInfoForm.get(inAWeekControlName).setValue(inAWeek);
            this.contractBillingInfoForm.get(in2WeeksControlName).setValue(in2Weeks);
        }
    }

    onTabKey(): void {
        if (!this.contract.isAyaBrand || this.contract.isSubcontractor) {
            // must have setTimeout() to work with mat-form-field
            setTimeout(() => {
                this.agencyRegularPayInput.nativeElement.focus();
                this.agencyRegularPayInput.nativeElement.select();
            });
        }
    }

    calculateDefaultRates(isAgencyPay: boolean): void {
        this.defaultStartDate = this.contractStartDate ? this.contractStartDate : new Date();

        if (this.billingRules && this.billingRules.rules.length) {
            const overtimeRule = this.billingRules.rules.find((x) => x.name === 'Overtime');
            const doubleTimeRule = this.billingRules.rules.find((x) => x.name === 'Double Time');
            const chargeNurseRule = this.billingRules.rules.find((x) => x.name === 'Charge Nurse');
            const onCallRule = this.billingRules.rules.find((x) => x.name === 'On Call');
            const callBackRule = this.billingRules.rules.find((x) => x.name === 'Call Back');
            const holidayRule = this.billingRules.rules.find((x) => x.name === 'Holiday');
            if (isAgencyPay) {
                this.formatDecimal('agencyRegPay');
                const regularRate = parseFloat(this.contractBillingInfoForm.get('agencyRegPay').value);
                this.calculateRate(overtimeRule, 'agencyOTPay', regularRate, Big.roundDown);
                this.calculateRate(doubleTimeRule, 'agencyDTPay', regularRate);
                this.calculateRate(chargeNurseRule, 'agencyChargePay', regularRate);
                this.calculateRate(onCallRule, 'agencyCallPay', regularRate);
                this.calculateRate(callBackRule, 'agencyCBPay', regularRate, Big.roundDown);
                this.calculateRate(holidayRule, 'agencyHOLPay', regularRate, Big.roundDown);
            }

            this.formatDecimal('billRegRate');
            const regularRate = parseFloat(this.contractBillingInfoForm.get('billRegRate').value);
            this.calculateRate(overtimeRule, 'billOverTimeRate', regularRate, Big.roundDown);
            this.calculateRate(doubleTimeRule, 'billDoubleTimeRate', regularRate);
            this.calculateRate(chargeNurseRule, 'billChargeRate', regularRate);
            this.calculateRate(onCallRule, 'billOnCallRate', regularRate);
            this.calculateRate(callBackRule, 'billCallBackRate', regularRate, Big.roundDown);
            this.calculateRate(holidayRule, 'billHolidayRate', regularRate, Big.roundDown);
            this.calculateFreq(overtimeRule, 'billOverTimeDayRule', 'billOverTimeWeekRule', 'billOverTimeTwoWeekRule');
            this.calculateFreq(
                doubleTimeRule,
                'billDoubleTimeDayRule',
                'billDoubleTimeWeekRule',
                'billDoubleTimeTwoWeekRule'
            );
            this.initialLoading = false;
        }
    }

    formatDecimal(controlName: string) {
        let value = this.contractBillingInfoForm.get(controlName).value;

        if (this.displayLocumPayrollEffectiveDatesHistoryFlag) {
            //Null Rates set to zero
            if (value === null) {
                value = 0.0;
            }
        }

        // empty string should be force to numeric NaN
        if (typeof value === 'string' && value.replace(' ', '') === '') {
            value = NaN;
        }
        if (!isEmpty(value) && !isNaN(value)) {
            this.contractBillingInfoForm.get(controlName).setValue(parseFloat(value).toFixed(2));
        } else if (!isEmpty(value) && value.toString() !== 'NaN') {
            this.contractBillingInfoForm.get(controlName).setValue('0.00');
        } else {
            this.contractBillingInfoForm.get(controlName).setValue(NaN);
        }
    }

    formatOrientationHours(hours?: number, rate?: number, type = ''): string {
        const nonBillable = ' Non-Billable';

        let result = `${hours || 0} hours`;
        result += rate && rate > 0 ? (type.trim() === '$' ? ` at ${type}${rate}` : ` at ${rate}${type}`) : nonBillable;

        return result;
    }

    saveBillingInfo(cleanedBillingData: UntypedFormGroup, isIntialSave = false): void {
        this.pendingSave = true;
        this.travelContractsService
            .saveBillingInfo(this.contract.id.toString(), cleanedBillingData.value)
            .pipe(
                mergeMap(() => {
                    if (isIntialSave && cleanedBillingData?.value.startDate) {
                        return this.effectiveDatesService.addBillingRate(
                            this.contract.id,
                            this.mapDataToUpdate(cleanedBillingData.value)
                        );
                    } else {
                        return of({});
                    }
                }),
                catchError((error) => {
                    this.toasterService.toast('An error occurred while saving Billing Info Data');
                    console.error(error);
                    return throwError(error);
                })
            )
            .subscribe({
                error: (err) => console.error(err),
                complete: () => {
                    this.pendingSave = false;
                    this.hasSavedBillingRate = true;
                    this.reloadTrigger.next(true);
                    this.defaultStartDate = null;
                    return this.toasterService.toast('Billing Info Data was successfully saved!');
                }
            });
    }

    /**
     * Converts the Billing data as follows for Bill Rates and Agency Pay.
     * 1. If Overtime === NaN, changes to Regular Rate when saved
     * 2. If Double Time === NaN, changes to Regular Rate when saved
     * 3. If Holiday = NaN, changes to Regular Rate when saved
     */
    convertNaNData(): UntypedFormGroup {
        const billRateList = ['billOverTimeRate', 'billDoubleTimeRate', 'billHolidayRate'];
        const agencyPayList = ['agencyOTPay', 'agencyDTPay', 'agencyHOLPay'];
        const cleanedData: UntypedFormGroup = this.createForm();
        cleanedData.patchValue(this.contractBillingInfoForm.value);

        // convert "Bill Rates"
        const regRate = this.contractBillingInfoForm.get('billRegRate').value;
        Object.keys(this.contractBillingInfoForm.controls).forEach((name) => {
            const value = this.contractBillingInfoForm.get(name).value;
            if (billRateList.includes(name) && (this.initialLoading || isNaN(value))) {
                cleanedData.get(name).setValue(regRate);
            }
        });

        // convert "Agency Pay"
        if (this.contract.isSubcontractor || !this.contract.isAyaBrand) {
            const agencyRate = this.contractBillingInfoForm.get('agencyRegPay').value;
            Object.keys(this.contractBillingInfoForm.controls).forEach((name) => {
                const value = this.contractBillingInfoForm.get(name).value;
                if (agencyPayList.includes(name) && (this.initialLoading || isNaN(value))) {
                    cleanedData.get(name).setValue(agencyRate);
                }
            });
        }
        if (this.defaultStartDate) {
            cleanedData.value.startDate = this.defaultStartDate;
        }

        if (!cleanedData.value.startDate && this.isNew) {
            cleanedData.value.startDate = this.contractStartDate ? this.contractStartDate : new Date();
        }
        return cleanedData;
    }

    save(): void {
        this.initialLoading = false;
        const cleanedBillingData = this.convertNaNData();
        if (!this.isNew && (this.isSummaryChanged || this.defaultStartDate)) {
            this.openSummaryModal(cleanedBillingData);
        } else {
            this.saveBillingInfo(cleanedBillingData, true);
            this.isNew = false;
        }
    }

    downloadDocument(): void {
        if (this.documentId)
            this.travelContractsService.downloadDocument(this.documentId).subscribe((response) => {
                const link = document.createElement('a');
                link.download = 'Work Order Confirmation';
                if (response) link.href = window.URL.createObjectURL(response);
                link.click();
                link.remove();
            });
    }

    getContractType(type: ContractType): string {
        if (type === ContractType.Evening || type === ContractType.Night || type === ContractType.Rotate) {
            return ContractType[type];
        }
        return ContractType[ContractType.Day];
    }

    openSummaryModal(cleanedBillingData: UntypedFormGroup): void {
        const billingMargin = this.marginDifferencesForBillingsOnly().length > 0;

        this._dialog
            .open(EffectiveDatesSummaryComponent, {
                position: { right: '0px' },
                data: {
                    oldValue: billingMargin ? this.previousValues : this.initialValues,
                    newValue: cleanedBillingData.value,
                    isDisplayAgencyPay: this.contract.isSubcontractor || !this.contract.isAyaBrand,
                    contractId: this.contract.id,
                    policyId: this.policyId,
                    startDate: this.startDate,
                    contractStartDate: this.contractStartDate,
                    contractEndDate: this.contractEndDate,
                    setEffectiveDate: billingMargin ? true : false,
                    effectiveDate: this.billRateEffectiveDate,
                    isLocum: this.isLocum
                },
                panelClass: 'effective-dates-dialog',
                height: '100%',
                minWidth: '700px',
                disableClose: true,
                autoFocus: false
            })
            .afterClosed()
            .subscribe((data) => {
                if (data) {
                    const cleanedBillingData = this.convertNaNData();
                    this.saveBillingInfo(cleanedBillingData, false);
                }
            });
    }

    openHistoryModal() {
        this.effectiveDatesHistoryRef = this._dialog.open(EffectiveDatesHistoryComponent, {
            position: { right: '0px' },
            data: {
                contractId: this.contract.id,
                isDisplayAgencyPay: this.contract.isSubcontractor || !this.contract.isAyaBrand,
                contractStartDate: this.contractStartDate,
                contractEndDate: this.contractEndDate,
                editMode: this.isEditRates,
                isLocum: this.isLocum
            },
            panelClass: 'effective-dates-dialog',
            height: '100%',
            minWidth: '800px',
            disableClose: true,
            autoFocus: false
        });
        this.effectiveDatesHistoryRef.afterClosed().subscribe((data) => {
            if (data) {
                this.hasSavedBillingRate = true;
                this.reloadTrigger.next(true);
            }
        });
    }

    mapDataToUpdate(data): BillingPolicy {
        return {
            ...data,
            id: data.policyId,
            agencyCallBackRate: !data.agencyCBPay ? 0 : data.agencyCBPay,
            agencyOnCallRate: !data.agencyCallPay ? 0 : data.agencyCallPay,
            agencyChargeRate: !data.agencyChargePay ? 0 : data.agencyChargePay,
            agencyDTRate: !data.agencyDTPay ? 0 : data.agencyDTPay,
            agencyHolidayRate: !data.agencyHOLPay ? 0 : data.agencyHOLPay,
            agencyOTRate: !data.agencyOTPay ? 0 : data.agencyOTPay,
            agencyRegularRate: !data.agencyRegPay ? 0 : data.agencyRegPay,

            agencyNightCallRate: !data.agencyNightCallPay ? 0 : data.agencyNightCallPay,
            agencyOnCall24HourRate: !data.agencyOnCall24HourPay ? 0 : data.agencyOnCall24HourPay,
            agencyWeekendRate: !data.agencyWeekendPay ? 0 : data.agencyWeekendPay,

            billDtRate: !data.billDoubleTimeRate ? 0 : data.billDoubleTimeRate,
            billOtRate: !data.billOverTimeRate ? 0 : data.billOverTimeRate,
            billRegularRate: !data.billRegRate ? 0 : data.billRegRate,
            billCallBackRate: !data.billCallBackRate ? 0 : data.billCallBackRate,
            billChargeRate: !data.billChargeRate ? 0 : data.billChargeRate,
            billOnCallRate: !data.billOnCallRate ? 0 : data.billOnCallRate,
            billHolidayRate: !data.billHolidayRate ? 0 : data.billHolidayRate,
            billNightCallRate: !data.billNightCallRate ? 0 : data.billNightCallRate,
            billOnCall24HourRate: !data.billOnCall24HourRate ? 0 : data.billOnCall24HourRate,
            billWeekendRate: !data.billWeekendRate ? 0 : data.billWeekendRate,
            oTDayRule: !data.billOverTimeDayRule ? 0 : data.billOverTimeDayRule,
            oTWeekRule: !data.billOverTimeWeekRule ? 0 : data.billOverTimeWeekRule,
            oT2WeekRule: !data.billOverTimeTwoWeekRule ? 0 : data.billOverTimeTwoWeekRule,
            dTDayRule: !data.billDoubleTimeDayRule ? 0 : data.billDoubleTimeDayRule,
            dTWeekRule: !data.billDoubleTimeWeekRule ? 0 : data.billDoubleTimeWeekRule,
            dT2WeekRule: !data.billDoubleTimeTwoWeekRule ? 0 : data.billDoubleTimeTwoWeekRule
        };
    }

    private hasSummaryChanges(fields, summary1, summary2): boolean {
        return fields.some((field) => summary1[field] !== summary2[field]);
    }

    get isBillRatesChanged(): boolean {
        return this.hasSummaryChanges(
            this.summaryGroups.billRates,
            this.initialValues,
            this.contractBillingInfoForm.value
        );
    }

    get isAgencyPayChanged(): boolean {
        return this.hasSummaryChanges(
            this.summaryGroups.agencyPay,
            this.initialValues,
            this.contractBillingInfoForm.value
        );
    }

    get isOvertimeRulesChanged(): boolean {
        return this.hasSummaryChanges(
            this.summaryGroups.overtimeRules,
            this.initialValues,
            this.contractBillingInfoForm.value
        );
    }

    get isDoubletimeRulesChanged(): boolean {
        return this.hasSummaryChanges(
            this.summaryGroups.doubletimeRules,
            this.initialValues,
            this.contractBillingInfoForm.value
        );
    }

    get isSummaryChanged(): boolean {
        return [
            this.isBillRatesChanged,
            this.isAgencyPayChanged,
            this.isOvertimeRulesChanged,
            this.isDoubletimeRulesChanged
        ].some((changed) => changed);
    }

    fillNotesInfo(data: BillingRates): void {
        const currentItem = data.billingPolicies?.find(
            (item) => item.timeTrackingStatus === TimeTrackingStatus.Current
        );
        const futureItems = data.billingPolicies?.filter(
            (item) => item.timeTrackingStatus === TimeTrackingStatus.Future
        );
        const pastItems = data.billingPolicies?.filter((item) => item.timeTrackingStatus === TimeTrackingStatus.Past);

        if (currentItem) {
            let startDate = currentItem.startDate;
            if (this.invalidDates.includes(currentItem.startDate?.toString())) {
                startDate = this.contractStartDate;
            }

            this.billRateEffectiveDate =
                this.contract?.contractStageDescription === ContractStatus.PRE ||
                this.contract?.contractStageDescription === ContractStatus.PER_DIEM
                    ? startDate
                    : this.billRateEffectiveDate;
            this.billRatesCurrentInfo = this.agencyPayCurrentInfo = `Eff: ${this.getFormattedDate(startDate)}`;
        }

        if (currentItem && futureItems.length) {
            this.billRatesFutureInfo = this.getBillRatesFutureInfo(futureItems);
            this.agencyPayFutureInfo = this.getAgencyPayFutureInfo(futureItems);
            this.overtimeRulesFutureInfo = this.getOvertimeRulesFutureInfo(currentItem, futureItems);
            this.doubletimeRulesFutureInfo = this.getDoubletimeRulesFutureInfo(currentItem, futureItems);
        }

        if (currentItem && pastItems.length) {
            this.billRatesPastInfo = this.getBillRatesPastInfo(pastItems);
            this.agencyPayPastInfo = this.getAgencyPayPastInfo(pastItems);
        }
    }

    private getBillRatesFutureInfo(futureItems: BillingPolicy[]): string {
        let message = ``;

        for (const i of futureItems) {
            message += `Future Rate: $${i.billRegularRate} (Eff: ${this.getFormattedDate(i.startDate)})\n`;
        }

        return message;
    }

    private getBillRatesPastInfo(pastItems: BillingPolicy[]): string {
        let message = ``;
        pastItems = pastItems.filter((x) => x.endDate >= this.contractStartDate);
        for (const i of pastItems) {
            let startDate = i?.startDate;
            if (this.invalidDates.includes(i.startDate?.toString())) {
                startDate = this.contractStartDate;
            }
            message += `Prev Rate: $${i.billRegularRate} (Eff: ${this.getFormattedDate(startDate)})\n`;
        }
        return message;
    }

    private getAgencyPayFutureInfo(futureItems: BillingPolicy[]): string {
        let message = ``;

        for (const i of futureItems) {
            message += `Future Rate: $${i.agencyRegularRate} (Eff: ${this.getFormattedDate(i.startDate)})\n`;
        }

        return message;
    }

    private getAgencyPayPastInfo(pastItems: BillingPolicy[]): string {
        let message = ``;
        pastItems = pastItems.filter((x) => x.endDate >= this.contractStartDate);
        for (const i of pastItems) {
            let startDate = i?.startDate;
            if (this.invalidDates.includes(i.startDate?.toString())) {
                startDate = this.contractStartDate;
            }
            message += `Prev Rate: $${i.agencyRegularRate} (Eff: ${this.getFormattedDate(startDate)})\n`;
        }
        return message;
    }

    private getOvertimeRulesFutureInfo(currentItem: BillingPolicy, futureItems: BillingPolicy[]): string {
        const nearestChange = futureItems.find((item) =>
            this.hasSummaryChanges(this.summaryGroups.overtimeRules, currentItem, item)
        );

        if (!nearestChange) {
            return '';
        }

        let message = `Next Effective Date: ${this.getFormattedDate(nearestChange.startDate)}`;

        if (currentItem.otDayRule !== nearestChange.otDayRule) {
            message += `\nin a day: ${Number(currentItem.otDayRule)} -> ${Number(nearestChange.otDayRule)}`;
        }
        if (currentItem.otWeekRule !== nearestChange.otWeekRule) {
            message += `\nin a week: ${Number(currentItem.otWeekRule)} -> ${Number(nearestChange.otWeekRule)}`;
        }
        if (currentItem.oT2WeekRule !== nearestChange.oT2WeekRule) {
            message += `\nin 2 weeks: ${Number(currentItem.oT2WeekRule)} -> ${Number(nearestChange.oT2WeekRule)}`;
        }
        return message;
    }

    private getDoubletimeRulesFutureInfo(currentItem: BillingPolicy, futureItems: BillingPolicy[]): string {
        const nearestChange = futureItems.find((item) =>
            this.hasSummaryChanges(this.summaryGroups.doubletimeRules, currentItem, item)
        );

        if (!nearestChange) {
            return '';
        }

        let message = `Next Effective Date: ${this.getFormattedDate(nearestChange.startDate)}`;

        if (currentItem.dtDayRule !== nearestChange.dtDayRule) {
            message += `\nin a day: ${Number(currentItem.dtDayRule)} -> ${Number(nearestChange.dtDayRule)}`;
        }
        if (currentItem.dtWeekRule !== nearestChange.dtWeekRule) {
            message += `\nin a week: ${Number(currentItem.dtWeekRule)} -> ${Number(nearestChange.dtWeekRule)}`;
        }
        if (currentItem.dT2WeekRule !== nearestChange.dT2WeekRule) {
            message += `\nin 2 weeks: ${Number(currentItem.dT2WeekRule)} -> ${Number(nearestChange.dT2WeekRule)}`;
        }
        return message;
    }

    private overrideBillRegRate(info: ContractBillingInfo): void {
        // if margin -> baseRate available
        if (this.margin && this.margin.baseRate) {
            if (this.marginDifferences && this.marginDifferences.length > 0) {
                const rates = this.marginDifferences.filter(
                    (x) =>
                        (x.fieldName === 'Base Pay Rate' ||
                            x.fieldName === 'Base Bill Rate' ||
                            x.fieldName === 'Number Of Shifts') &&
                        x.effectiveDate
                );
                if (rates.length) {
                    const marginDifferencesBillRate = rates.find(
                        (x) => x.groupId === MarginEffectiveDateGroupId.BillRate
                    );
                    this.billRateEffectiveDate = marginDifferencesBillRate.effectiveDate;
                }

                info.billRegRate = this.margin.baseRate;
                info.agencyRegPay = this.margin.baseRate;
            }
        }
    }

    getFormattedDate(date: Date): string {
        return dayjs(date).format('MM/DD/YY');
    }

    hasBillingRateFromMarginDifferences = (): boolean =>
        this.marginDifferences?.some((x) => x.fieldName === this.baseBillRate) ?? false;

    marginDifferencesForBillingsOnly = (): MarginDifference[] =>
        this.marginDifferences?.filter(
            (x) =>
                x.fieldName === this.baseBillRate ||
                x.fieldName === this.otBillRate ||
                x.fieldName === this.premiumBillRate ||
                x.fieldName === this.chargeBill ||
                x.fieldName === this.totalChargeBill ||
                x.fieldName === this.onCallBillRate ||
                x.fieldName === this.callbackBillRate ||
                x.fieldName === this.holidayBillRate
        ) ?? [];

    setDefaultsFromMarginDifferences(contractBillingInfo: ContractBillingInfo): ContractBillingInfo {
        const contractBilling: ContractBillingInfo = contractBillingInfo
            ? contractBillingInfo
            : this.getBlankContractBillingInfo();

        if (this.hasSavedBillingRate) {
            return contractBilling;
        }
        const items = this.marginDifferencesForBillingsOnly();

        if (items.some((x) => x)) {
            //I only do this subroutine if there are margin differences
            this.previousValues = { ...contractBillingInfo };
            this.defaultStartDate = this.contractStartDate;

            const baseBill = items.filter((x) => x.fieldName === this.baseBillRate)[0];

            if (baseBill) {
                //If there is a base bill ignore everything but the base bill passed in.
                contractBilling.billRegRate = +baseBill.currentMarginValue.replace('$', '');
                this.overrideBillRegRate(contractBilling);
            } else {
                const rates = this.marginDifferences.filter(
                    (x) =>
                        (x.fieldName === this.otBillRate ||
                            x.fieldName === this.callbackBillRate ||
                            x.fieldName === this.holidayBillRate ||
                            x.fieldName === this.onCallBillRate ||
                            x.fieldName === this.chargeBill) &&
                        x.effectiveDate
                );
                if (rates.length) {
                    const marginDifferencesBillRate = rates.find(
                        (x) => x.groupId === MarginEffectiveDateGroupId.BillRate
                    );
                    this.billRateEffectiveDate = marginDifferencesBillRate?.effectiveDate;
                }

                //If there are other billing items deal with them individually.
                const totalChargeBill = Number(
                    items.find((x) => x.fieldName === this.totalChargeBill)?.currentMarginValue.replace('$', '') ?? 0
                );

                contractBilling.billOverTimeRate = Number(
                    items.find((x) => x.fieldName === this.otBillRate)?.currentMarginValue.replace('$', '') ??
                        contractBillingInfo.billOverTimeRate
                );
                contractBilling.billDoubleTimeRate = Number(
                    items.find((x) => x.fieldName === this.premiumBillRate)?.currentMarginValue.replace('$', '') ??
                        contractBillingInfo.billDoubleTimeRate
                );
                contractBilling.billChargeRate =
                    totalChargeBill > 0 ? totalChargeBill : Number(contractBillingInfo.billChargeRate);
                contractBilling.billOnCallRate = Number(
                    items.find((x) => x.fieldName === this.onCallBillRate)?.currentMarginValue.replace('$', '') ??
                        contractBillingInfo.billOnCallRate
                );
                contractBilling.billCallBackRate = Number(
                    items.find((x) => x.fieldName === this.callbackBillRate)?.currentMarginValue.replace('$', '') ??
                        contractBillingInfo.billCallBackRate
                );
                contractBilling.billHolidayRate = Number(
                    items.find((x) => x.fieldName === this.holidayBillRate)?.currentMarginValue.replace('$', '') ??
                        contractBillingInfo.billHolidayRate
                );

                this.setAgencyPaySectionDefaultsFromBillRatesValues(contractBilling);
            }
        }

        return contractBilling;
    }

    private setAgencyPaySectionDefaultsFromBillRatesValues(info: ContractBillingInfo) {
        info.agencyOTPay = info.billOverTimeRate;
        info.agencyChargePay = info.billChargeRate;
        info.agencyCallPay = info.billOnCallRate;
        info.agencyCBPay = info.billCallBackRate;
        info.agencyHOLPay = info.billHolidayRate;
    }

    private getBlankContractBillingInfo(): ContractBillingInfo {
        this.isNew = true;
        return {
            startDate: new Date(),
            contractId: this.contract?.id,
            policyId: 0,
            payRollTypeCode: false,
            billRegRate: 0,
            billOverTimeRate: 0,
            billDoubleTimeRate: 0,
            billChargeRate: 0,
            billOnCallRate: 0,
            billCallBackRate: 0,
            billHolidayRate: 0,
            billNightCallRate: 0,
            billOnCall24HourRate: 0,
            billWeekendRate: 0,
            billOverTimeDayRule: 0,
            billOverTimeWeekRule: 0,
            billOverTimeTwoWeekRule: 0,
            billDoubleTimeDayRule: 0,
            billDoubleTimeWeekRule: 0,
            billDoubleTimeTwoWeekRule: 0,
            billOrientation: 0,
            billOrientation2: 0,
            notes: '',
            specialRate: false,
            documentId: 0,
            agencyCallPay: 0,
            agencyCBPay: 0,
            agencyChargePay: 0,
            agencyDTPay: 0,
            agencyHOLPay: 0,
            agencyOTPay: 0,
            agencyRegPay: 0,
            agencyNightCallPay: 0,
            agencyOnCall24HourPay: 0,
            agencyWeekendPay: 0,
            nonBillableOrientationHours: []
        };
    }

    private scrollToBillRateSection = (): void => this.billRatesSection?.nativeElement?.scrollIntoView();

    getNonBillableOrientationHours(): UntypedFormArray {
        return this.contractBillingInfoForm.controls.nonBillableOrientationHours as UntypedFormArray;
    }

    getNBOrientationFormGroup(index: number): UntypedFormGroup {
        return this.getNonBillableOrientationHours().controls[index] as UntypedFormGroup;
    }

    isNonStandardShiftWithInfo(): boolean {
        return this.isNonStandardShiftWithInfoFlag
            ? this.contract.contractScheduleId &&
                  this.contract.contractScheduleId === ContractSchedule.Alternating &&
                  this.contract.contractNumshifts2 &&
                  this.contract.contractShiftHours2 &&
                  this.contract.contractShiftHours2 > 0
            : this.contract.contractScheduleId &&
                  this.contract.contractScheduleId > 1 &&
                  this.contract.contractNumshifts2 &&
                  this.contract.contractShiftHours2 &&
                  this.contract.contractShiftHours2 > 0;
    }

    private attachFeatureFlag(featureFlag: FeatureFlag) {
        this._lDFeatureManager
            .isEnabled(featureFlag)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                if (featureFlag === PathfindersFlags.BillingProfileRotatingShiftFix) {
                    this.isNonStandardShiftWithInfoFlag = flagIsEnabled;
                } else if (featureFlag === PayrollTimecardFeatureFlags.DisplayLocumPayrollEffectiveDatesHistory) {
                    this.displayLocumPayrollEffectiveDatesHistoryFlag = flagIsEnabled;
                }
            });
    }
}
